import paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import {
  projectConstructionLines,
  type ConstructionLine,
  type ConstructionOutline,
  type Vec2,
} from "../model/Project";
import { buildConstructionOutlinePath } from "../model/constructionOutline";
import {
  buildConstructionArrangement,
  faceAt,
  faceAndNeighborsAt,
  faceKey,
  mergeFacesOutline,
  type ConstructionArrangement,
} from "../model/constructionArrangement";
import type { FaceEdge } from "../model/planarFaces";
import {
  commitConstructionShape,
  type ShapeMode,
} from "./constructionShapeMode";
import { cssVar } from "../colors";

/**
 * Region click-to-fill (#651): overlapping construction lines partition the
 * plane into faces. Click regions to add them to the selection (click again to
 * remove); adjacent regions merge automatically, their shared edges dropped.
 * Finish with Enter / the fly-out check to commit the selection as one leather
 * piece whose outline is a `constructionOutline` traced from the surrounding
 * segments (so it re-derives when the lines change). Escape / the fly-out ✕
 * discards the selection.
 */
export class RegionTool extends ToolBase {
  id = "region";
  label = "Region";
  shortcut = "f";
  cursor = "crosshair";

  private arrangement: ConstructionArrangement | null = null;
  private arrangementKey = "";
  /** Faces selected so far (keyed by faceKey), pending a finish. */
  private selected = new Map<string, FaceEdge[]>();
  private previewItems: paper.Item[] = [];
  /** What a filled region becomes — leather piece / cutout / stitch line. */
  private mode: ShapeMode = "leather";
  /** Notified when the selection changes so the done/cancel fly-out updates. */
  private draftChange?: () => void;

  setMode(mode: ShapeMode) {
    this.mode = mode;
  }

  /** Number of selected regions (drives the fly-out). */
  get selectedCount(): number {
    return this.selected.size;
  }

  setDraftChangeListener(fn: () => void) {
    this.draftChange = fn;
  }

  override onActivate() {
    this.arrangement = null;
    this.arrangementKey = "";
    this.clear();
  }

  override onDeactivate() {
    this.clear();
  }

  override onCancel() {
    this.clear();
  }

  private clear() {
    this.selected.clear();
    this.clearPreview();
    this.draftChange?.();
  }

  private lines(): ConstructionLine[] {
    return projectConstructionLines(this.ctx.history.state);
  }

  /** Rebuild the planar arrangement only when the construction lines change
   *  (noding is O(lines²)). Stale face indices invalidate the selection. The
   *  key carries visibility because hidden lines don't partition the plane —
   *  hiding one mid-tool must merge the regions it split (#1104). */
  private currentArrangement(): ConstructionArrangement {
    const lines = this.lines();
    const key = lines
      .map((l) => `${l.id}:${l.visible === false ? 0 : 1}:${l.pathData}`)
      .join("|");
    if (this.arrangement && key === this.arrangementKey)
      return this.arrangement;
    this.arrangementKey = key;
    if (this.selected.size > 0) {
      this.selected.clear();
      this.draftChange?.();
    }
    this.arrangement = buildConstructionArrangement(
      lines,
      this.ctx.scene.getPaperScope(),
    );
    return this.arrangement;
  }

  private faceUnder(p: Vec2): FaceEdge[] | null {
    return faceAt(this.currentArrangement(), p);
  }

  override onHover(_s: PointerSample, p: { x: number; y: number }) {
    this.updatePreview(p);
  }

  override onMove(_s: PointerSample, p: { x: number; y: number }) {
    this.updatePreview(p);
  }

  override onDown(_s: PointerSample, p: { x: number; y: number }) {
    const arr = this.currentArrangement();
    const info = faceAndNeighborsAt(arr, p);
    if (!info) return; // clicked outside any enclosed region

    // An isolated region (no adjacent face to merge with) and nothing already
    // selected → commit it straight away, no finish step needed.
    if (this.selected.size === 0 && !info.hasNeighbor) {
      const outline = mergeFacesOutline([info.face], arr);
      if (outline) this.commitOutline(outline);
      this.updatePreview(p);
      return;
    }

    // Otherwise build a selection: toggle the clicked region.
    const k = faceKey(info.face);
    if (this.selected.has(k)) this.selected.delete(k);
    else this.selected.set(k, info.face);
    this.draftChange?.();
    this.updatePreview(p);
  }

  /** Enter / fly-out check: commit the selected regions as one piece. */
  handleFinish(): boolean {
    if (this.selected.size === 0) return false;
    const arr = this.currentArrangement();
    const outline = mergeFacesOutline([...this.selected.values()], arr);
    if (!outline) return false;
    if (!this.commitOutline(outline)) return false;
    this.clear();
    return true;
  }

  private commitOutline(outline: ConstructionOutline): boolean {
    return commitConstructionShape(this.ctx, outline, this.mode);
  }

  /** Escape / fly-out ✕: discard the selection. */
  handleCancel(): boolean {
    if (this.selected.size === 0) return false;
    this.clear();
    return true;
  }

  private clearPreview() {
    for (const it of this.previewItems) it.remove();
    this.previewItems = [];
  }

  private fillOutline(
    outline: ConstructionOutline,
    opts: { fillAlpha: number; dashed: boolean },
  ) {
    const scope = this.ctx.scene.getPaperScope();
    const d = buildConstructionOutlinePath(outline, this.lines(), scope);
    if (!d) return;
    const item = new scope.CompoundPath({ pathData: d });
    if (this.mode === "cutout") {
      // Preview a filled region destined to be a cutout with the cutout look —
      // translucent black fill + dashed cut-coloured edge (#701).
      item.fillColor = new scope.Color(0, 0, 0, opts.fillAlpha);
      item.strokeColor = new scope.Color(cssVar("--piece-cut"));
      item.dashArray = [3, 3];
    } else {
      const col = new scope.Color(cssVar("--leather-fill"));
      col.alpha *= opts.fillAlpha;
      item.fillColor = col;
      item.strokeColor = new scope.Color(cssVar("--canvas-accent"));
      if (opts.dashed) item.dashArray = [4, 2];
    }
    item.strokeWidth = 1.5;
    item.strokeScaling = false;
    item.data = { ephemeral: true };
    this.previewItems.push(item);
  }

  private updatePreview(p: Vec2) {
    this.clearPreview();
    const arr = this.currentArrangement();
    // Selected regions (solid) — exactly what a finish will commit.
    if (this.selected.size > 0) {
      const outline = mergeFacesOutline([...this.selected.values()], arr);
      if (outline) this.fillOutline(outline, { fillAlpha: 0.6, dashed: false });
    }
    // The region under the cursor, if not already selected — lighter / dashed
    // to read as "click to add".
    const hovered = this.faceUnder(p);
    if (hovered && !this.selected.has(faceKey(hovered))) {
      const outline = mergeFacesOutline([hovered], arr);
      if (outline) this.fillOutline(outline, { fillAlpha: 0.25, dashed: true });
    }
  }
}
